import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import "@ag-grid-community/styles/ag-grid.css";
import "@ag-grid-community/styles/ag-theme-alpine.css";
import { SquarePen, Trash2, ChevronUp, ChevronDown } from "lucide-react";
import { toast } from "react-toastify";
import ListView from "../../shared/ListView";
import ConfirmModal from "../../shared/ConfirmModal";

// Import both single & bulk delete mutations
import {
  useDeleteBranchMutation,
  useBulkDeleteBranchMutation,
  useGetBranchesQuery
} from "../../../../services/redux/api/components";

const Branch = () => {
  const navigate = useNavigate();
  const gridRef = useRef(null);

  const [deleteBranch, { isLoading: isDeletingSingle }] = useDeleteBranchMutation();
  const [bulkDeleteBranch, { isLoading: isDeletingBulk }] = useBulkDeleteBranchMutation();

  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const [deleteId, setDeleteId] = useState(null); // for single delete
  const [deleteMode, setDeleteMode] = useState("single"); // "single" or "multi"
  const [isOpen, setIsOpen] = useState(false); // modal open
  const [selectedNames, setSelectedNames] = useState([]);
  const selectedIdsRef = useRef([]);
  const [allSelected, setAllSelected] = useState(false);
  const [preserveSelection, setPreserveSelection] = useState(false);

  // Filter & Sort states
  const [filterColumn, setFilterColumn] = useState("Branch_Name");
  const [filterValueInput, setFilterValueInput] = useState(""); // user typing
  const [filterValue, setFilterValue] = useState("");           // used in API
  const [columnOptions, setColumnOptions] = useState([]);
  const [isFilterApplied, setIsFilterApplied] = useState(false);
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);

  const {
    data: branches,
    isLoading,
    error,
    refetch, 
  } = useGetBranchesQuery({
    pageNumber: currentPage,
    pageSize: itemsPerPage,
    sortField,
    sortOrder: sortDirection,
    ...(isFilterApplied && {
      searchTerm: filterColumn,
      searchValue: filterValue,
    }),
  });

  useEffect(() => {
    const options = [
      { value: "Branch_Name", label: "Branch Name" },
      { value: "Branch_ShortName", label: "Short Name" },
      { value: "Manager_Name", label: "Manager Name" },
      { value: "Manager_Contact_No", label: "Contact Number" },
      { value: "City", label: "City" },
      { value: "State", label: "State" },
      { value: "PinCode", label: "Pincode" }
    ];
    setColumnOptions(options);
  }, []);

  const rowData = useMemo(() => {
    if (!branches?.data) return [];
    return Array.isArray(branches.data)
      ? branches.data.map(item => ({
        ...item,
        id: item.Branch_ID,
      }))
      : [];
  }, [branches, isFilterApplied]);

  const handleFilter = () => {
    if (!filterValueInput.trim()) {
      toast.warning("Please enter filter value");
      return;
    }
    setFilterValue(filterValueInput); // will trigger API fetch
    setIsFilterApplied(true);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilterValueInput("");
    setFilterValue("");
    setIsFilterApplied(false);
    setCurrentPage(1);
  };

  const handleSort = (field) => {
    let direction = "asc";
    if (sortField === field) {
      direction = sortDirection === "asc" ? "desc" : sortDirection === "desc" ? null : "asc";
    }
    setSortField(direction ? field : null);
    setSortDirection(direction);
    setCurrentPage(1);
  };

  const createHeaderComponent = (field, displayName) => {
    return () => {
      const isSorted = sortField === field;
      const isAsc = sortDirection === "asc";

      return (
        <div
          className="cursor-pointer hover:text-brandRed flex items-center"
          onClick={() => handleSort(field)}
        >
          {displayName}
          {isSorted && (
            <span className="ml-1">
              {isAsc ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </span>
          )}
        </div>
      );
    };
  };

  const [columnDefs, setColumnDefs] = useState([]);

  useEffect(() => {
    const createColumnDefs = () => [
      {
        headerCheckboxSelection: true,
        checkboxSelection: true,
        width: 50,
        suppressMenu: true,
      },
      {
        headerName: "Branch Name",
        field: "Branch_Name",
        flex: 1,
        headerComponent: createHeaderComponent("Branch_Name", "Branch Name"),
        suppressMenu: true,
      },
      {
        headerName: "Short Name",
        field: "Branch_ShortName",
        flex: 1,
        headerComponent: createHeaderComponent("Branch_ShortName", "Short Name"),
        suppressMenu: true,
      },
      {
        headerName: "Manager Name",
        field: "Manager_Name",
        flex: 1,
        headerComponent: createHeaderComponent("Manager_Name", "Manager Name"),
        suppressMenu: true,
      },
      {
        headerName: "Contact Number",
        field: "Manager_Contact_No",
        flex: 1,
        headerComponent: createHeaderComponent("Manager_Contact_No", "Contact Number"),
        suppressMenu: true,
      },
      {
        headerName: "City",
        field: "City",
        flex: 1,
        headerComponent: createHeaderComponent("City", "City"),
        suppressMenu: true,
      },
      {
        headerName: "State",
        field: "State",
        flex: 1,
        headerComponent: createHeaderComponent("State", "State"),
        suppressMenu: true,
      },
      {
        headerName: "Pincode",
        field: "PinCode",
        flex: 1,
        headerComponent: createHeaderComponent("PinCode", "Pincode"),
        suppressMenu: true,
      },
      {
        headerName: "Action",
        field: "action",
        headerClass: 'action-header',
        cellRenderer: (params) => (
          <div className="flex space-x-2 mt-1">
            <button
              className="edit-button"
              onClick={() => navigate(`/branch/edit/${params.data.Branch_ID}`)}
              title="Edit"
              onMouseDown={(e) => e.preventDefault()}
            >
              <SquarePen color="#787474" size={18} className="mt-1" />
            </button>
            <button
              className="delete-button"
              onClick={(e) => {
                e.stopPropagation();
                onOpenSingleDelete(params.data.Branch_ID, params.data.Branch_Name);
              }}
              title="Delete"
              onMouseDown={(e) => e.preventDefault()}
            >
              <Trash2 color="#787474" size={18} className="mt-1" />
            </button>
          </div>
        ),
      },
    ];

    setColumnDefs(createColumnDefs());
  }, [sortField, sortDirection]); // you may update this dependency list as needed

  // Handles when selection checkbox changes
  const onSelectionChanged = () => {
    if (isOpen || !gridRef.current?.api) return;

    const selectedNodes = gridRef.current.api.getSelectedNodes() || [];
    const totalDisplayedRows = gridRef.current.api.getDisplayedRowCount() || 0;

    selectedIdsRef.current = selectedNodes.map((node) => node.data.Branch_ID);
    setSelectedNames(selectedNodes.map((node) => node.data.Branch_Name));

    setAllSelected(selectedNodes.length === totalDisplayedRows && totalDisplayedRows > 0);
    setDeleteMode(selectedNodes.length > 1 || allSelected ? "multi" : "single");
  };

  const handleDeleteClick = () => {
    const count = selectedIdsRef.current.length;
    if (count === 0) {
      toast.warning("Please select at least one branch to delete");
      return;
    }

    setDeleteMode(allSelected || count > 1 ? "multi" : "single");
    setDeleteId(allSelected ? null : selectedIdsRef.current[0]);
    // set selectedNames already done by onSelectionChanged
    setIsOpen(true);
  };

  // Function for opening delete modal for single-row delete (Trash2)
  const onOpenSingleDelete = (id, name) => {
    selectedIdsRef.current = [id];
    setSelectedNames([name]);
    setDeleteId(id);
    setDeleteMode("single");
    setIsOpen(true);
  };

  const onClose = () => {
    setIsOpen(false);
    setAllSelected(false);
    setPreserveSelection(false);
  };

  const confirmDelete = async () => {
    try {
      if (deleteMode === "single" && deleteId) {
        const response = await deleteBranch(deleteId).unwrap();
        if (response.success) {
          toast.success(response.message || "Branch deleted successfully");
          refetch();
        } else {
          toast.error(response.message || "Failed to delete branch");
        }
      } else {
        const idsToDelete = allSelected
          ? rowData.map((x) => x.Branch_ID)
          : selectedIdsRef.current;

        const response = await bulkDeleteBranch({ ids: idsToDelete }).unwrap();

        if (response.success) {
          toast.success(response.message || `${idsToDelete.length} branches deleted successfully`);
          refetch();
        } else {
          toast.error(response.message || "Failed to delete branches");
        }
      }
    } catch (err) {
      toast.error(err?.data?.message || "Something went wrong");
    } finally {
      onClose();
    }
  };

  const handleItemsPerPageChange = (event) => {
    const newPageSize = parseInt(event.target.value, 10);
    setItemsPerPage(newPageSize);
    setCurrentPage(1);
  };

  useEffect(() => {
    const totalPages = Math.ceil((branches?.totalCount || 0) / itemsPerPage);
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [branches?.totalCount, currentPage, itemsPerPage]);

  useEffect(() => {
    if (preserveSelection && selectedIdsRef.current.length > 0 && gridRef.current?.api) {
      gridRef.current.api.forEachNode((node) => {
        if (selectedIdsRef.current.includes(node.data.Branch_ID)) {
          node.setSelected(true);
        }
      });
    }
  }, [rowData, currentPage, preserveSelection]);

  return (
    <div className="container p-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold text-brandRed">Branch Master List</h2>
        <div className="flex gap-2">
          <button
            className="px-4 py-2 addListTable-button"
            onClick={() => navigate("/branch/add")}
          >
            Add
          </button>
          {selectedNames.length > 0 && (
            <button
              className="px-4 py-2 addListTable-button"
              onClick={handleDeleteClick}
            >
              Delete
            </button>
          )}
        </div>
      </div>

      <ListView
        filterColumn={filterColumn}
        setFilterColumn={setFilterColumn}
        filterValue={filterValueInput}
        setFilterValue={setFilterValueInput}
        columnOptions={columnOptions}
        handleFilter={handleFilter}
        clearFilters={clearFilters}
        isFilterApplied={isFilterApplied}
        gridRef={gridRef}
        columnDefs={columnDefs}
        rowData={rowData}
        itemsPerPage={itemsPerPage}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        onSelectionChanged={onSelectionChanged}
        defaultColDef={{ sortable: true, resizable: true, suppressMenu: true }}
        loading={isLoading}
        handleItemsPerPageChange={handleItemsPerPageChange}
        totalCount={branches?.totalCount || 0}
      />

      <ConfirmModal
        isOpen={isOpen}
        onClose={onClose}
        onConfirm={confirmDelete}
        title="Confirm Deletion"
        message={
          allSelected
            ? `Are you sure you want to delete all ${rowData.length} branches on this page?`
            : deleteMode === "multi"
              ? `Are you sure you want to delete ${selectedIdsRef.current.length} selected branches?`
              : `Are you sure you want to delete this branch: ${selectedNames[0]}?`
        }
        selectedNames={selectedNames}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        confirmColor="backButton"
      />
    </div>
  );
};

export default Branch;